import _ from 'lodash'
import dayjs from 'dayjs'
import isBetween from 'dayjs/plugin/isBetween'
import { useContext, useEffect, useMemo, useRef } from 'react'
import { useFormikContext } from 'formik'
import { msg } from '@lingui/macro'
import { useLingui } from '@lingui/react'

import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Step, { StepProps } from '@mui/material/Step'
import StepContent from '@mui/material/StepContent'
import StepLabel from '@mui/material/StepLabel'
import Typography from '@mui/material/Typography'
import WhereToVoteRoundedIcon from '@mui/icons-material/WhereToVoteRounded'

import GooglePlacesAutocompleteField from '@/components/formik/fields/GooglePlacesAutocompleteField'
import SelectField from '@/components/formik/fields/SelectField'

import OrderStepCard from '@/components/ui/cards/OrderStepCard'
import RadioGroupField from '@/components/formik/fields/RadioGroupField'
import { SelectOption } from '@/components/ui/inputs/SelectInput'

import { AvailabilityMeetingPlace } from '@/core/models/meeting-link.model'

import { BookingPageContext } from '@/pages/booking/BookingPage'

import { BookingFormContext, BookingFormValues } from './BookingForm'
import BottomBar from './BottomBar'
import { DateTimeStepProps } from './DateTimeStep'
import Summary from './Summary'

dayjs.extend(isBetween)

const addressStepMessages = {
  label: msg`Meeting place`,
  address: msg`Address`,
  center: msg`Center`,
  atHome: msg`At patient's home`,
  videoConsultation: msg`Video consultation`,
}

export type AddressStepProps = StepProps & {
  schedules: DateTimeStepProps['schedules']
}

export function AddressStep(props: AddressStepProps) {
  const { active, completed, index = 0, last, schedules, ...rest } = props
  const { isMobile, onComplete, onEditStep, onNextStep, onPreviousStep } = useContext(BookingFormContext)
  const { changeAddress } = useContext(BookingPageContext)
  const { setFieldTouched, setFieldValue, values } = useFormikContext<BookingFormValues>()
  const { i18n } = useLingui()

  const valuesRef = useRef<typeof values>(values)

  const stepProps = {
    active,
    completed,
    index,
    last,
    ...rest,
  }

  const { address, meetingPlace, speciality } = values
  const label = i18n._(addressStepMessages.label)
  const addressLabel = i18n._(addressStepMessages.address)
  const videoConsultationLabel = i18n._(addressStepMessages.videoConsultation)

  const availabilityMeetingPlaceOptions = useMemo(
    () => [
      { label: i18n._(addressStepMessages.center), value: AvailabilityMeetingPlace.CENTER },
      { label: i18n._(addressStepMessages.atHome), value: AvailabilityMeetingPlace.AT_HOME },
      { label: videoConsultationLabel, value: AvailabilityMeetingPlace.VISIO_CONFERENCE },
    ],
    [i18n, i18n.locale, videoConsultationLabel]
  )

  const establishmentAddressOptions = useMemo(() => {
    const addressOptions: SelectOption[] = []
    const addresses = _.map(
      _.filter(schedules, (schedule) => _.includes(schedule.specialities, speciality)),
      'address'
    )
    for (const address of addresses) {
      if (address !== null && address !== undefined) addressOptions.push({ label: address.description, value: address.placeId })
    }
    return _.uniqBy(addressOptions, 'value')
  }, [schedules, speciality])

  useEffect(() => {
    valuesRef.current = values
  }, [values])

  useEffect(() => {
    setFieldTouched('address', false, false)
    setFieldValue(
      'address',
      meetingPlace === AvailabilityMeetingPlace.VISIO_CONFERENCE
        ? { description: videoConsultationLabel, placeId: AvailabilityMeetingPlace.VISIO_CONFERENCE }
        : null
    )
  }, [setFieldTouched, setFieldValue, meetingPlace, speciality, videoConsultationLabel])

  useEffect(() => {
    if (!address) return

    const establishmentAddressOption = _.find(establishmentAddressOptions, (option) => option.value === address.placeId)
    if (!!establishmentAddressOption) {
      setFieldValue('address.description', establishmentAddressOption.label)
    }
  }, [address, establishmentAddressOptions, setFieldValue])

  useEffect(() => {
    changeAddress?.(address)
  }, [address, changeAddress])

  if (!active && !completed) {
    return isMobile ? null : (
      <Typography variant="h3" mt={1} mb={1}>
        {label}
      </Typography>
    )
  }

  const addressValue = address?.description || ''

  if (isMobile) {
    return (
      <Step {...stepProps}>
        {completed ? null : (
          <StepContent sx={{ height: '100%' }}>
            <Stack height="100%" spacing={2}>
              <Typography variant="h2" mt={1} mb={1}>
                {label}
              </Typography>
              <Box flex={1}>
                <Paper sx={{ borderRadius: '6px', p: 2 }}>
                  {meetingPlace !== AvailabilityMeetingPlace.VISIO_CONFERENCE && (
                    <Box height="70px">
                      {meetingPlace === AvailabilityMeetingPlace.AT_HOME && !_.find(establishmentAddressOptions, ({ value }) => value === address?.placeId) && (
                        <GooglePlacesAutocompleteField fullWidth label={addressLabel} name="address" />
                      )}
                      {meetingPlace === AvailabilityMeetingPlace.CENTER && (
                        <SelectField fullWidth label={addressLabel} name="address.placeId" options={establishmentAddressOptions} />
                      )}
                    </Box>
                  )}
                </Paper>
              </Box>
              <BottomBar disabled={!addressValue} isFirst={index === 0} isLast={last} onComplete={onComplete} onNext={onNextStep} onPrevious={onPreviousStep} />
            </Stack>
          </StepContent>
        )}
      </Step>
    )
  }

  return (
    <Step {...stepProps}>
      {completed ? (
        <StepLabel icon={<WhereToVoteRoundedIcon />}>
          <Summary
            label={label}
            onEdit={() => onEditStep(index)}
            text={meetingPlace === AvailabilityMeetingPlace.VISIO_CONFERENCE ? videoConsultationLabel : addressValue}
          />
        </StepLabel>
      ) : (
        <StepContent>
          <OrderStepCard
            disabled={!addressValue && meetingPlace !== AvailabilityMeetingPlace.VISIO_CONFERENCE}
            isFirst={index === 0}
            isLast={last}
            title={label}
            onComplete={onComplete}
            onNext={onNextStep}
            onPrevious={onPreviousStep}
          >
            <Stack spacing={2} sx={{ p: '10px 30px 35px' }}>
              <RadioGroupField name="meetingPlace" options={availabilityMeetingPlaceOptions} variant="contained" />
              {meetingPlace !== AvailabilityMeetingPlace.VISIO_CONFERENCE && (
                <Box height="70px">
                  {meetingPlace === AvailabilityMeetingPlace.AT_HOME && !_.find(establishmentAddressOptions, ({ value }) => value === address?.placeId) && (
                    <GooglePlacesAutocompleteField fullWidth label={addressLabel} name="address" />
                  )}
                  {meetingPlace === AvailabilityMeetingPlace.CENTER && (
                    <SelectField fullWidth label={addressLabel} name="address.placeId" options={establishmentAddressOptions} />
                  )}
                </Box>
              )}
            </Stack>
          </OrderStepCard>
        </StepContent>
      )}
    </Step>
  )
}

export default AddressStep
